import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useListVatBanksQuery,
  useListVatInvoicesQuery,
  useSubmitVatMutation,
  type VatInvoiceItem,
} from '@/store/api/payDataVatApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  cpCode: string
  supplierLabel: string
  paydataIds: number[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Access `btnSave_Click` guard messages (verbatim copy → English). */
const PAID_DATE_REQUIRED = 'Please choose VAT Paid Date!'
const BANK_REQUIRED = 'Please choose VAT Paid Bank Account!'

/** Access Format = "* #,##0.00;..." → Standard 2dp display. */
const fmtAmount = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Access Format = "d-mmm-yy" → DD-MMM-YYYY display. */
const fmtDate = (v: string | null | undefined): string =>
  v ? dayjs(v).format('DD-MMM-YYYY') : ''

const rowKey = (it: VatInvoiceItem): string => `${it.invoice_no ?? ''}|${it.contract_no ?? ''}`

/**
 * Pay Data → "Send - DNTT VAT" dialog — converts Access form
 * `frmProcurementQryPayDataSendVATAdd` (+ subform `frmProcurementQryPayDataSendVAT`).
 *
 * Flow: vendor + InvoiceDate (From-To) filter the VAT-paid invoice list
 * (`refresh_vat_paid`); user ticks rows (Tick All), chooses VAT Paid Date +
 * Bank, then Send submits the VAT declaration (`btnSave_Click`).
 *
 * No fullScreen; mobile stacks the filter grid to one column and the list
 * scrolls horizontally inside its own container.
 */
export default function PayDataSendVatDialog({
  open,
  cpCode,
  supplierLabel,
  paydataIds,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  // ----- Filter state (Filter Area) -----
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [contractNo, setContractNo] = useState<string>('')

  // ----- Input Data state -----
  const [paidDate, setPaidDate] = useState<string>('')
  const [bankAccount, setBankAccount] = useState<string>('')

  // ----- Tick selection (checked_row) -----
  const [ticked, setTicked] = useState<Set<string>>(new Set())

  // Re-init each time the dialog opens (hydrate only on [open]).
  useEffect(() => {
    if (!open) return
    setDateFrom('')
    setDateTo('')
    setContractNo('')
    setPaidDate('')
    setBankAccount('')
    setTicked(new Set())
  }, [open])

  // refresh_vat_paid — invoice list filtered by cp_code + contract_no + date range.
  const { data: invData, isFetching: invLoading } = useListVatInvoicesQuery(
    open
      ? {
          cp_code: cpCode || undefined,
          contract_no: contractNo || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
      : { cp_code: '__none__' },
    { skip: !open, refetchOnMountOrArgChange: true },
  )
  const invoices = useMemo(() => invData?.items ?? [], [invData])

  // Bank lookup (QryCPBank by cp_code).
  const { data: bankData } = useListVatBanksQuery(open ? { cp_code: cpCode || undefined } : undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const banks = useMemo(() => bankData?.items ?? [], [bankData])

  // Access: when the list refreshes, default bank_account = first option.
  useEffect(() => {
    if (!open) return
    setBankAccount((prev) => {
      if (prev && banks.some((bk) => bk.bank_account === prev)) return prev
      return banks.length > 0 ? (banks[0].bank_account ?? '') : ''
    })
  }, [open, banks])

  // Distinct contract numbers from the list → contract_no Autocomplete options.
  const contractOptions = useMemo(() => {
    const set = new Set<string>()
    for (const it of invoices) if (it.contract_no) set.add(it.contract_no)
    return Array.from(set).sort()
  }, [invoices])

  const [submitVat, { isLoading: saving }] = useSubmitVatMutation()

  const allTicked = invoices.length > 0 && invoices.every((it) => ticked.has(rowKey(it)))
  const someTicked = invoices.some((it) => ticked.has(rowKey(it)))

  const toggleTickAll = () => {
    if (allTicked) {
      setTicked(new Set())
    } else {
      setTicked(new Set(invoices.map(rowKey)))
    }
  }
  const toggleRow = (it: VatInvoiceItem) => {
    const key = rowKey(it)
    setTicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedInvoices = useMemo(
    () => invoices.filter((it) => ticked.has(rowKey(it))),
    [invoices, ticked],
  )

  const paidDateMissing = !paidDate || paidDate.trim() === ''
  const bankMissing = !bankAccount || bankAccount.trim() === ''
  const canSave =
    canEdit && !saving && !paidDateMissing && !bankMissing && selectedInvoices.length > 0

  const handleSend = async () => {
    if (!canEdit) return
    if (paidDateMissing) {
      snack.warning(PAID_DATE_REQUIRED)
      return
    }
    if (bankMissing) {
      snack.warning(BANK_REQUIRED)
      return
    }
    if (selectedInvoices.length === 0) {
      snack.warning('No records to choose!')
      return
    }
    try {
      const res = await submitVat({
        paid_date: paidDate,
        bank_account: bankAccount,
        cp_code: cpCode || null,
        invoices: selectedInvoices.map((it) => ({
          invoice_no: it.invoice_no ?? '',
          contract_no: it.contract_no ?? '',
          vat_amount: it.vat_amount ?? null,
        })),
      }).unwrap()
      snack.success(res.message || 'VAT declaration submitted')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to submit VAT declaration'))
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <Box
        sx={{
          pb: 1,
          pt: 1.25,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`Send VAT — ${supplierLabel || cpCode}`}
          sx={{
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Send VAT — {supplierLabel || cpCode}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSend()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Send'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
      <Divider />

      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for this action.
          </Alert>
        )}

        {/* ===== Filter Area ===== */}
        <Typography sx={{ ...sectionHeaderSx, mb: 1 }}>Filter Area</Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
          {/* Row 1: InvoiceDate (From-To) — date_from | date_to */}
          <DatePicker
            label="Invoice Date (From)"
            format="DD-MMM-YYYY"
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(v) => setDateFrom(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 } },
              },
            }}
          />
          <DatePicker
            label="Invoice Date (To)"
            format="DD-MMM-YYYY"
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(v) => setDateTo(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 2 } },
              },
            }}
          />

          {/* Row 2: Vendor (cp_code, fixed from open context) | CP Name */}
          <TextField
            label="Vendor"
            size="small"
            fullWidth
            value={cpCode ?? ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...roSx, gridColumn: { xs: 1, sm: 1 } }}
          />
          <TextField
            label="CP Name"
            size="small"
            fullWidth
            value={supplierLabel ?? ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...roSx, gridColumn: { xs: 1, sm: 2 } }}
          />

          {/* Row 3: Contract No filter (Autocomplete from list) */}
          <Autocomplete
            size="small"
            options={contractOptions}
            value={contractNo || null}
            onChange={(_e, v) => setContractNo(v ?? '')}
            isOptionEqualToValue={(a, b) => a === b}
            sx={{ gridColumn: { xs: 1, sm: 1 } }}
            renderInput={(params) => (
              <TextField {...params} label="Contract No" InputLabelProps={{ shrink: true }} />
            )}
          />
        </Box>

        {/* ===== Input Data ===== */}
        <Typography sx={{ ...sectionHeaderSx, mt: 2, mb: 1 }}>Input Data</Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
          {/* paid_date (required) */}
          <DatePicker
            label="VAT Paid Request - Date *"
            format="DD-MMM-YYYY"
            value={paidDate ? dayjs(paidDate) : null}
            onChange={(v) => setPaidDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            disabled={!canEdit}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: paidDateMissing && someTicked,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 } },
              },
            }}
          />

          {/* bank_account (required) — VAT Paid Request - Bank */}
          <Autocomplete
            size="small"
            options={banks}
            value={banks.find((bk) => bk.bank_account === bankAccount) ?? null}
            onChange={(_e, v) => setBankAccount(v?.bank_account ?? '')}
            getOptionLabel={(o) =>
              o ? (o.bank_info ? `${o.bank_account} — ${o.bank_info}` : (o.bank_account ?? '')) : ''
            }
            isOptionEqualToValue={(a, b) => a.bank_account === b.bank_account}
            disabled={!canEdit}
            sx={{ gridColumn: { xs: 1, sm: 2 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="VAT Paid Request - Bank *"
                required
                error={bankMissing && someTicked}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Box>

        {/* ===== Tick All + hint ===== */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={0.5}
          sx={{ mt: 2, mb: 0.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Checkbox
              size="small"
              checked={allTicked}
              indeterminate={someTicked && !allTicked}
              onChange={toggleTickAll}
              disabled={!canEdit || invoices.length === 0}
            />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>Tick All</Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            Only send the ticked rows. You can filter the list, then click &apos;Tick All&apos;.
          </Typography>
          {invLoading && <CircularProgress size={14} />}
        </Stack>

        {/* ===== Invoice list (subForm frmProcurementQryPayDataSendVAT) ===== */}
        <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table stickyHeader size="small" sx={{ '& td, & th': { fontSize: '0.75rem', py: 0.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>ID</TableCell>
                <TableCell>InvoiceNo</TableCell>
                <TableCell>ContractNo</TableCell>
                <TableCell>CP Name</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">InvoiceVAT Amount</TableCell>
                <TableCell align="right">Invoice Amount (afterVAT)</TableCell>
                <TableCell>VATPaidRequest - Status</TableCell>
                <TableCell>VATPaidRequest - Date</TableCell>
                <TableCell align="right">VATPaidRequest - Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ color: 'text.secondary' }}>
                    {invLoading ? 'Loading…' : 'No invoices for the chosen filter.'}
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((it) => {
                  const key = rowKey(it)
                  const checked = ticked.has(key)
                  return (
                    <TableRow
                      key={`${key}|${it.invoice_id ?? ''}`}
                      hover
                      selected={checked}
                      onClick={() => canEdit && toggleRow(it)}
                      sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleRow(it)}
                          disabled={!canEdit}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>{it.invoice_id ?? ''}</TableCell>
                      <TableCell>{it.invoice_no ?? ''}</TableCell>
                      <TableCell>{it.contract_no ?? ''}</TableCell>
                      <TableCell>{it.contract_name ?? ''}</TableCell>
                      <TableCell>{fmtDate(it.invoice_date)}</TableCell>
                      <TableCell align="right" sx={numSx}>
                        {fmtAmount(it.total_amount)}
                      </TableCell>
                      <TableCell align="right" sx={numSx}>
                        {fmtAmount(it.vat_amount)}
                      </TableCell>
                      <TableCell align="right" sx={numSx}>
                        {fmtAmount(it.total_amount_after_tax)}
                      </TableCell>
                      <TableCell>{it.paid_vat_txt ?? ''}</TableCell>
                      <TableCell>{fmtDate(it.paid_vat_date)}</TableCell>
                      <TableCell align="right" sx={numSx}>
                        {fmtAmount(it.paid_vat_value)}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* paydataIds carried from the opening row(s) (Access source context). */}
        {paydataIds.length > 0 && (
          <Typography sx={{ mt: 0.5, fontSize: '0.68rem', color: 'text.disabled' }}>
            {selectedInvoices.length} invoice(s) selected.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  )
}
